import { DATA_BINDING } from '@wix/app-definition-ids'
import { itemViewShouldBeUsedInsteadOfCollectionView } from './business-logic'

import type {
  EditorSDK,
  DatabindingPublicApi,
  Logger,
} from '@wix/wix-data-client-common-standalone'
import type { ComponentRef } from '@wix/editor-platform-sdk-types'
import {
  openMultilingualDashboard,
  type AppApi,
} from '@wix/wix-data-client-common'
import { leftPanelMlCollectionSelected } from '@wix/bi-logger-multilingual-data/v2'
import type { MultilingualLanguages } from './types'
import { CollectionIds } from './constants'

export const MANAGE_CONTENT_PANEL_ORIGIN = 'manage_content_panel'
export const GFPP_CHANGE_CONTENT_ORIGIN = 'gfpp_change_content'
export const GFPP_ORIGIN = 'gfpp'
export const GFPP_AUTO_OPEN_ORIGIN = 'gfpp_auto_open'

export const openConnectPanel = async (
  editorSdkProxy: EditorSDK,
  componentRef: ComponentRef,
) => {
  const dataBindingAppApi =
    await editorSdkProxy.document.application.getPublicAPI<DatabindingPublicApi>(
      {
        appDefinitionId: DATA_BINDING,
      },
    )
  dataBindingAppApi.openConnectPanel({
    componentRef,
    origin: MANAGE_CONTENT_PANEL_ORIGIN,
  })

  editorSdkProxy.editor.closePanel(null)
}

interface openAutoCmsProps {
  editorSdkProxy: EditorSDK
  componentRef: ComponentRef
  appApi: AppApi
  origin: string
}

interface OpenWixServices {
  appApi: AppApi
}

export const openAutoCms = async ({
  editorSdkProxy,
  componentRef,
  appApi,
  origin,
}: openAutoCmsProps) => {
  const dataset = await appApi.fetchConnectedDataset(componentRef)

  const {
    config: { collectionId },
    type: datasetType,
  } = dataset

  if (collectionId === CollectionIds.INQUIRY_SERVICES) {
    return await openWixServices({ appApi })
  }

  const showItemView = await itemViewShouldBeUsedInsteadOfCollectionView({
    editorSdkProxy,
    appApi,
    componentRef,
    datasetType,
  })

  if (showItemView) {
    await appApi.openAutoCmsItemViewForCurrentDynamicPage({
      origin,
    })
  } else {
    await appApi.openAutoCmsCollectionView({
      collectionId,
      origin,
    })
  }
}

export const openWixServices = async ({ appApi }: OpenWixServices) => {
  await appApi.openWixServices()
}

interface OpenMultilingualProps {
  collectionId: string
  collectionName: string
  multilingualLanguages: MultilingualLanguages
  origin: string
  logger: Logger
  editorSdkProxy: EditorSDK
}

export const openMultilingual = ({
  collectionId,
  collectionName,
  multilingualLanguages,
  origin,
  logger,
  editorSdkProxy,
}: OpenMultilingualProps) => {
  logger.bi(
    leftPanelMlCollectionSelected({
      collection_id: collectionId,
      collection_name: collectionName,
      environment: 'manage-translations-panel',
      origin,
      primary_lang: multilingualLanguages.primaryLanguage,
      secondary_lang: multilingualLanguages.currentLanguage,
    }),
  )
  openMultilingualDashboard({
    secondaryLanguage: multilingualLanguages.currentLanguage,
    editorSdkProxy,
    logger,
  })
}
